import { DOCUMENT, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
  type CanActivateFn,
} from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { LocalStorageService } from '@cosmos/data-access-storage';
import { collectRouteParams } from '@cosmos/router';
import {
  CustomerPortalAuthActions,
  CustomerPortalAuthQueries,
  isPresentationUuid,
} from '@customer-portal/auth/data-access-auth';
import { setRetainAccessSearchParams } from '@customer-portal/common/util-retain-access-code';

import {
  getUrlTreeWithoutTokensOrPass,
  handleLoginErrorRedirect,
  tryToRestorePresentationSessionFromStorage,
} from './access.guard.util';

/**
 * Auth guard for the `/presentations/:presentationId` route.
 *
 * Authenticates the user via `accessCode` or `token` query param.
 * Strips `token` (and `accessCode`) from the URL after successful auth.
 * Does NOT redirect to a project-scoped URL — the presentation URL is stable.
 */
export const customerPortalPresentationAuthGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean | UrlTree> => {
  const store = inject(Store);
  const router = inject(Router);
  const storage = inject(LocalStorageService);
  const document = inject(DOCUMENT);

  const { presentationId } = collectRouteParams(route.root);
  const queryParams = route.queryParams;
  const accessCode: string | undefined = queryParams['accessCode'];
  const postPayment: string | undefined = queryParams['postPayment'];
  const token: string | undefined = postPayment
    ? undefined
    : queryParams['token'];

  if (!presentationId) {
    return router.navigate(['/not-found'], { skipLocationChange: true });
  }
  const isUuid = isPresentationUuid(presentationId);

  const session = store.selectSnapshot(CustomerPortalAuthQueries.getSession);
  const isUserDistributor = store.selectSnapshot(
    CustomerPortalAuthQueries.getUserIsDistributor
  );

  // Already authenticated with no explicit credential override (except for UUID-identified presentations).
  // A distributor session is exempt from that uuid carve-out: the distributor
  // token authorizes every presentation, uuid or numeric, so there is nothing
  // to re-login for — and the fallthrough below would dispatch
  // `LoginWithAccessCode`, which clears `isUserDistributor` and drops the
  // preview frame. The header links to a standalone presentation by uuid while
  // the preview link opens it by numeric id, so that param change re-activates
  // this route mid-session.
  if (session && !token && !accessCode && (isUserDistributor || !isUuid)) {
    return true;
  }

  // Login by explicit token (e.g. distributor preview link)
  if (token) {
    await firstValueFrom(
      store.dispatch(
        new CustomerPortalAuthActions.LoginWithDistributorToken({
          distributorToken: token,
        })
      ),
      { defaultValue: undefined }
    );
    return getUrlTreeWithoutTokensOrPass(router, state.url);
  }

  // Try to restore presentation session from local storage in case of:
  // - The user has no active session and no access code is provided in the URL.
  // - The presentation is identified by a UUID, since such presentation doesn't have access code at all.
  if ((!session && !accessCode) || isUuid) {
    if (
      await tryToRestorePresentationSessionFromStorage(store, storage, route)
    ) {
      return true;
    }
  }

  // Login with access code or UUID (or dummy code to probe entity existence)
  try {
    await firstValueFrom(
      store.dispatch(
        new CustomerPortalAuthActions.LoginWithAccessCode({
          // A standalone presentation is shared by uuid and has no access code
          // — the guid is the secret. Mars ignores the code on that path, so
          // none is sent from here; the api service substitutes a placeholder
          // for a login body that cannot yet omit it. For a numeric id the API
          // throws 400 without a code, so a dummy is passed to yield a 401 and
          // the redirect below.
          accessCode: isUuid ? undefined : accessCode || 'no-access-code',
          presentationId,
        })
      ),
      { defaultValue: undefined }
    );
    if (accessCode) {
      const url = new URL(document.location.href);
      // we need to set special query param to indicate that user was logged in via presentation share link and the access code retention is needed
      setRetainAccessSearchParams(url.searchParams);
      // keep special query param for presentation access code retention and remove token
      // so we can skip this auth guard logic next run (cause we returns url tree making this guard run again)
      return getUrlTreeWithoutTokensOrPass(router, url.pathname + url.search);
    }
    // - Return new UrlTree with the tokens stripped from the URL.
    //   Keep in mind that returning a UrlTree will cause the guard to run again.
    // - If there are no tokens in the URL, simply return "true" so we let navigation
    //   proceed to a child and so we don't get into a loop running this guard again.
    return getUrlTreeWithoutTokensOrPass(router, state.url);
  } catch (error) {
    return handleLoginErrorRedirect(error, router);
  }
};
